import { createFileRoute } from '@tanstack/react-router';
import { useContext, useState } from 'react';
import { useTranslations } from 'use-intl';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderSidebarTrigger } from '@/app/ui/components/header';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  FileBookmarkIcon,
  MoreHorizontalIcon,
  PencilEdit02Icon,
  PlusSignIcon,
} from '@hugeicons/core-free-icons';
import { createStepper, useStepper } from '@/app/ui/components/stepper';
import {
  Field,
  FieldControl,
  FieldError,
  FieldItem,
  FieldLabel,
} from '@/app/ui/components/form/field';
import { AppContext } from '@/app/appContext';
import { RadioGroup, RadioGroupLabel } from '@/app/ui/components/radio-group';
import { RadioGroupItem } from '@/app/ui/components/radio-group';
import { FieldsetLegend, Fieldset } from '@/app/ui/components/form/fieldset';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectTriggerIcon,
  SelectValue,
} from '@/app/ui/components/select';
import {
  useCreateExtractionsListTemplate,
  useGetAllExtractionListTemplates,
  useGetAllExtractionTemplates,
  useGetExtractionTemplateList,
} from '@/app/hooks/queries/useExtractionsTemplates';
import { atomWithStorage } from 'jotai/utils';
import { useAtom, useSetAtom } from 'jotai';
import { DroppedItem } from '@/app/types/utilities';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import ExtractionsDroppableArea from '@/app/components/features/evaluations/CreateForm/DragAndDrop/ExtractionsDroppableArea';
import SavedExtractionsList from '@/app/components/features/evaluations/CreateForm/SavedExtractionsList';
import SavedExtractionsListItem from '@/app/components/features/evaluations/CreateForm/DragAndDrop/SavedExtractionsListItem';
import { usePosthogTracking } from '@/app/components/posthog/usePosthogTracking';
import { SpecificEvent } from '@/app/utils/constants';
import ExtractionDetailsSheet from '@/app/components/features/evaluations/CreateForm/ExtractionDetailsSheet';
import { MainScrollArea } from '@/app/ui/components/main';
import AddOrganizationExtractionDialog from '@/app/components/features/evaluations/CreateForm/AddOrganizationExtractionDialog';
import { useNavigate } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { H1 } from '@/app/ui/components/heading';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/app/ui/components/menu';
import { Button } from '@/app/ui/components/button';
import { addOrganizationExtractionOpenAtom } from '@/app/components/features/organizations/extractions/store';

const {
  Stepper,
  StepperStep,
  StepperStepTitle,
  StepperStepDescription,
  StepperNavigation,
  StepperBack,
  StepperNext,
  StepperCancel,
  StepperStepScrollArea,
} = createStepper(['template_setup', 'add_extractions']);

const nameAtom = atomWithStorage('create_template_name', '');
const modeAtom = atomWithStorage<'createNew' | 'duplicate'>('create_template_mode', 'createNew');
const selectedTemplateIdAtom = atomWithStorage<string | null>('create_template_selected_id', null);
const extractionsAtom = atomWithStorage<DroppedItem[]>('create_template_extractions', []);

export const Route = createFileRoute('/_authed/(organization)/_layout/templates/new')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.templates');

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink to={'/templates'}>
                <HugeiconsIcon icon={FileBookmarkIcon} />
                {t('breadcrumbs.templates')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('breadcrumbs.newTemplate')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </Header>
      <MainScrollArea viewportClassName="flex grow md:bg-surface-2 md:p-3 min-h-0">
        <Stepper storageKey="create_template">
          <TemplateSetupStep />
          <AddExtractionsStep />
          <AddOrganizationExtractionDialog />
        </Stepper>
      </MainScrollArea>
    </>
  );
}

function TemplateSetupStep() {
  const tGeneral = useTranslations('general');
  const tPage = useTranslations('pages.templates');
  const t = useTranslations('pages.createEvalaution.modals.createExtractionsTemplates');
  const stepper = useStepper();
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta.organizationId ?? '';
  const [name, setName] = useAtom(nameAtom);
  const [mode, setMode] = useAtom(modeAtom);
  const [selectedTemplateId, setSelectedTemplateId] = useAtom(selectedTemplateIdAtom);
  const setExtractions = useSetAtom(extractionsAtom);
  const { trackPosthogEvent } = usePosthogTracking();

  const { data: existingTemplates } = useGetAllExtractionListTemplates({
    organizationId,
    enabled: !!organizationId,
  });

  const { data: selectedTemplateData } = useGetExtractionTemplateList({
    organizationId,
    customTopicTemplateListId: selectedTemplateId!,
    enabled: !!selectedTemplateId && mode === 'duplicate',
  });

  const handleNext = () => {
    if (mode === 'duplicate' && selectedTemplateData?.templates) {
      trackPosthogEvent(SpecificEvent.EXTRACTIONS_TEMPLATE_COPIED);
      setExtractions(
        selectedTemplateData.templates.map((t, index) => ({
          userQuery: t.userQuery,
          customTopicTemplateId: t.customTopicTemplateId,
          customTopicTemplateOrder: index,
          id: `dropped-${t.customTopicTemplateId}`,
        })),
      );
    }
    stepper.goNext();
  };

  return (
    <StepperStep
      id="template_setup"
      onFormSubmit={handleNext}
      className={'max-md:rounded-none max-md:border-transparent'}
    >
      <StepperStepScrollArea>
        <StepperStepTitle>{tPage('create.templateSetup.title')}</StepperStepTitle>
        <StepperStepDescription>{tPage('create.templateSetup.description')}</StepperStepDescription>
        <Field className={'max-w-sm'}>
          <FieldLabel>{t('droppableArea.formLabels.name.label')}</FieldLabel>
          <FieldControl
            autoFocus
            value={name}
            onValueChange={setName}
            required
            placeholder={t('droppableArea.formLabels.name.placeholder')}
          />
          <FieldError />
        </Field>
        <Field>
          <Fieldset
            render={<RadioGroup value={mode} onValueChange={(v) => setMode(v as typeof mode)} />}
          >
            <FieldsetLegend className={'md:mb-2 text-sm inline-block font-medium'}>
              {tPage('create.templateSetup.creationMode')}
            </FieldsetLegend>
            <FieldItem>
              <RadioGroupLabel>
                <RadioGroupItem value="createNew"></RadioGroupItem>
                {t('chooseMode.radioGroup.createNew')}
              </RadioGroupLabel>
            </FieldItem>
            <FieldItem>
              <RadioGroupLabel>
                <RadioGroupItem value="duplicate"></RadioGroupItem>
                {t('chooseMode.radioGroup.duplicate')}
              </RadioGroupLabel>
            </FieldItem>
          </Fieldset>
        </Field>
        {mode === 'duplicate' && (
          <Field className={'max-w-sm'}>
            <FieldLabel>{tPage('create.templateSetup.selectTemplate')}</FieldLabel>
            <Select
              required
              value={selectedTemplateId}
              onValueChange={(v) => setSelectedTemplateId(v)}
            >
              <SelectTrigger size="lg" className="w-full">
                <SelectValue>
                  {(v) =>
                    existingTemplates?.find((t) => t.id === v)?.name ??
                    tPage('create.templateSetup.selectPlaceholder')
                  }
                </SelectValue>
                <SelectTriggerIcon />
              </SelectTrigger>
              <SelectPopup>
                {existingTemplates?.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <FieldError />
          </Field>
        )}
      </StepperStepScrollArea>
      <StepperNavigation>
        <StepperCancel
          nativeButton={false}
          render={<Link to={'/templates'}>{tGeneral('cancel')}</Link>}
        />
        <StepperNext />
      </StepperNavigation>
    </StepperStep>
  );
}

function AddExtractionsStep() {
  const t = useTranslations('pages.createEvalaution.modals.createExtractionsTemplates');
  const tList = useTranslations(
    'pages.createEvalaution.modals.createExtractionsTemplates.savedExtractionsList',
  );
  const navigate = useNavigate();
  const stepper = useStepper();
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta.organizationId ?? '';
  const { trackPosthogEvent } = usePosthogTracking();

  const [name] = useAtom(nameAtom);
  const setName = useSetAtom(nameAtom);
  const setMode = useSetAtom(modeAtom);
  const setSelectedTemplateId = useSetAtom(selectedTemplateIdAtom);
  const [extractions, setExtractions] = useAtom(extractionsAtom);
  const setIsCreateDialogOpen = useSetAtom(addOrganizationExtractionOpenAtom);

  const [activeItemId, setActiveItemId] = useState<UniqueIdentifier | null>(null);
  const [selectedExtractionId, setSelectedExtractionId] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data: extractionTemplates } = useGetAllExtractionTemplates({
    organizationId,
    enabled: !!organizationId,
  });

  const { mutate: createTemplate, isPending } = useCreateExtractionsListTemplate({
    cb: (id) => {
      trackPosthogEvent(SpecificEvent.EXTRACTIONS_TEMPLATE_CREATED);
      // Reset form
      setName('');
      setMode('createNew');
      setSelectedTemplateId(null);
      setExtractions([]);
      stepper.goToFirst();
      navigate({ to: '/templates/$templateId', params: { templateId: id } });
    },
  });

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveItemId(active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const activeIndex = extractions.findIndex((item) => item.id === activeId);
    const overIndex = extractions.findIndex((item) => item.id === overId);

    if (activeIndex !== -1 && overIndex !== -1) {
      if (activeIndex !== overIndex) {
        setExtractions((prev) => arrayMove(prev, activeIndex, overIndex));
      }
    } else {
      const template = extractionTemplates?.find((t) => t.id === activeId);
      if (!template) return;

      const insertIndex = overIndex !== -1 ? overIndex : extractions.length;

      const newItem: DroppedItem = {
        userQuery: template.userQuery,
        customTopicTemplateId: template.id,
        customTopicTemplateOrder: insertIndex,
        id: `dropped-${template.id}`,
      };

      setExtractions((prev) => {
        const copy = [...prev];
        copy.splice(insertIndex, 0, newItem);
        return copy;
      });
    }
  };

  const handleDelete = (fieldId: string) => {
    setExtractions((prev) => prev.filter((item) => item.id !== fieldId));
  };

  const handleShowDetails = (customTopicTemplateId: string) => {
    setSelectedExtractionId(customTopicTemplateId);
    setIsSheetOpen(true);
  };

  const handleSubmit = () => {
    if (!extractions.length) return;

    createTemplate({
      organizationId,
      payload: {
        name,
        templates: extractions,
      },
    });
  };

  return (
    <StepperStep
      id="add_extractions"
      onFormSubmit={handleSubmit}
      className={'max-md:rounded-none max-md:border-transparent'}
    >
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="p-4 flex items-center gap-2">
          <H1 className="max-md:text-base max-md:line-clamp-1">{name}</H1>
          <Menu>
            <MenuTrigger
              render={
                <Button kind="icon" variant="ghost">
                  <HugeiconsIcon icon={MoreHorizontalIcon} className="size-5" />
                </Button>
              }
            />
            <MenuPopup align="start">
              <MenuItem onClick={() => stepper.goBack()}>
                <HugeiconsIcon icon={PencilEdit02Icon} />
                {t('editName')}
              </MenuItem>
            </MenuPopup>
          </Menu>
          <Button
            className="ml-auto"
            variant="secondary"
            kind="with-icon"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <HugeiconsIcon icon={PlusSignIcon} />
            {tList('buttons.createNew')}
          </Button>
        </div>
        <div className="grid @4xl:grid-cols-2 min-h-0 @4xl:divide-y-0 divide-y @4xl:divide-x grow border-t">
          <ExtractionsDroppableArea
            items={extractions}
            handleDelete={handleDelete}
            onShowDetails={handleShowDetails}
          />
          <SavedExtractionsList organizationId={organizationId} fields={extractions} />
        </div>
        <StepperNavigation className="p-4 rounded-b-[inherit] bg-background z-50">
          <StepperBack />
          <StepperNext pending={isPending} disabled={!extractions.length || isPending}>
            {t('droppableArea.buttons.create')}
          </StepperNext>
        </StepperNavigation>
        {createPortal(
          <DragOverlay>
            {activeItemId ? (
              <SavedExtractionsListItem
                id="active-item"
                title={
                  (activeItemId.toString().includes('dropped')
                    ? extractionTemplates?.find((t) => `dropped-${t.id}` === activeItemId)
                        ?.userQuery
                    : extractionTemplates?.find((t) => t.id === activeItemId)?.userQuery) ?? ''
                }
                disabled={false}
                dragOverlay
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
        <ExtractionDetailsSheet
          organizationId={organizationId}
          extractionId={selectedExtractionId}
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
        />
      </DndContext>
    </StepperStep>
  );
}
